/**
 * Full analysis orchestration (brief 16.4), implemented behind the
 * runAnalysis stub in engine.ts.
 *
 * Pipeline: direct estimates (9.3) -> design-effect smoothing (9.4)
 * -> transformation (9.5) -> per model cell: covariate assembly and
 * selection (9.14), minimum-conditions gate (9.15), Fay-Herriot fit
 * (9.6-9.9), back-transformation (9.10), Full-mode bootstrap MSEs
 * (9.12), optional external benchmarking (9.13), estimate policy
 * (9.11) with complements -> reliability measures (9.17) -> disclosure
 * rules and complementary suppression (Section 10).
 *
 * Policy decisions wired here (decision log Addendum 2, S4/S5/S6;
 * reasoning in validation/parity/OPEN_QUESTIONS.md Q17):
 * - Candidate areas are the union of areas seen in the microdata,
 *   the area-covariate table, and the population counts. An area
 *   with no direct estimate in a cell is EXCLUDED from that cell's
 *   model and NAMED in a MISSING_DIRECT_ESTIMATE issue (S4); with
 *   out-of-sample estimation enabled it may still receive a
 *   model-only estimate from its covariate row.
 * - A cell fits only when at least config.model.minAreas areas have
 *   effective sample size above 0 (brief 9.15); otherwise the cell
 *   reports direct estimates with status 'insufficient-areas'. A fit
 *   that still fails the absolute floor of five areas surfaces the
 *   same status with the InsufficientAreasError's MET-14 issue.
 *   Fitted cells with fewer than ten areas carry a LOW_AREA_COUNT
 *   warning (S4).
 * - The transformation is the configured one (default arcsine,
 *   auto-chosen by outcome type — proportions arcsine, counts
 *   linear — at the configuration layer) and is declared on every
 *   CellResult, with the MSE method and benchmarking state (brief
 *   12.3 model metadata).
 * - Benchmarking is on by default (D-4, 2026-10-17, superseding
 *   S5's off-by-default) unless a configuration turns it off. The target is the
 *   cell's population-weighted direct estimate — sum_i omega_i
 *   phat_i over the in-sample fitted areas with population-count
 *   weights (directive C-5; the STATEWIDE_AREA_ID domain the direct
 *   side emits per cell, OPEN_QUESTIONS Q10, is the survey-weighted
 *   form and is used for the aggregation diagnostic, not as the
 *   benchmarking target). Population counts come from the analysis
 *   input when supplied, else from the covariate rows; a cell whose
 *   counts are incomplete is left unbenchmarked with an explanatory
 *   issue, as is a cell where the ratio method is rejected
 *   (brief 9.13). Internal consistency benchmarking is not
 *   implemented (no input carries per-level population shares);
 *   enabling it records a warning per fitted cell and the run
 *   proceeds without it.
 * - Disclosure rules evaluate over every reported domain, including
 *   model-only domains (n = 0) and direct-only domains of
 *   insufficient cells: the rules are applied as written, and the
 *   outcome is stamped, never dropped (S6; enforcement at export).
 * - A singular design during fitting drops the most recently added
 *   non-locked selected covariate and retries (the least invasive
 *   expression of the brief's covariate-dropping fallback; see
 *   OPEN_QUESTIONS Q18 on N2). Locked covariates are never dropped;
 *   if only locked covariates remain, the cell reports direct
 *   estimates with a SINGULAR_DESIGN blocking issue.
 */
import { DEFAULT_FAY_HERRIOT_FIT_CONFIG } from './config.js';
import { assembleBootstrapEstimatesImpl, backTransformAreasImpl, } from './backtransform.js';
import { applyBenchmarkingImpl } from './benchmarking.js';
import { parametricBootstrapMseImpl } from './bootstrap.js';
import { conditionalCompositeMseTheta } from './conditional-mse.js';
import { smoothDesignEffectsImpl } from './deff.js';
import { computeV2Components } from './v2components.js';
import { computeDirectEstimatesImpl, kornGraubardBounds, STATEWIDE_AREA_ID, } from './direct.js';
import { applyComplementarySuppressionImpl, applyDisclosureRulesImpl, } from './disclosure.js';
import { InsufficientAreasError } from './errors.js';
import { fitFayHerriotImpl } from './fayherriot.js';
import { f3Candidate } from './frontierf3.js';
import { maxCovariatesFor, selectCovariatesImpl } from './selection.js';
import { clamp } from './numeric.js';
import { exactBinomialArcsineVariance, transformDomainsImpl, } from './transform.js';
import { assessValidatedRange, outOfValidatedRangeIssue, resolveValidatedRangeConfig, } from './validated-range.js';
const ENGINE_VERSION = '0.0.0';
/** Moment skewness of a sample (used by the brief 9.14 Auto log screen). */
function skewnessOf(values) {
    const n = values.length;
    if (n < 3)
        return 0;
    const mean = values.reduce((a, v) => a + v, 0) / n;
    let m2 = 0;
    let m3 = 0;
    for (const v of values) {
        const d = v - mean;
        m2 += d * d;
        m3 += d * d * d;
    }
    m2 /= n;
    m3 /= n;
    if (m2 <= 0)
        return 0;
    return m3 / (m2 * Math.sqrt(m2));
}
function domainKeyOf(domain) {
    return `${domain.cellId}${domain.areaId}`;
}
/**
 * The Fay-Herriot fit configuration for a run: the brief defaults,
 * plus the study-only switches the model config selects (Tier 2
 * Item 5's adjusted-REML-for-all; frontier F-2's model class). Used
 * at the observed fit and in the bootstrap options alike, so the
 * bootstrap's replicate fits estimate the same estimator the run
 * reports. Absent every study flag this is
 * DEFAULT_FAY_HERRIOT_FIT_CONFIG itself, unchanged.
 */
function fitConfigFor(config) {
    const studyModelClass = config.model.studyModelClass;
    const studyQuadratureNodes = config.model.studyQuadratureNodes;
    if (config.model.studyAdjustedRemlForAll !== true &&
        studyModelClass !== 'fh-t' &&
        studyQuadratureNodes === undefined) {
        return DEFAULT_FAY_HERRIOT_FIT_CONFIG;
    }
    return {
        ...DEFAULT_FAY_HERRIOT_FIT_CONFIG,
        ...(config.model.studyAdjustedRemlForAll === true
            ? { studyAdjustedRemlForAll: true }
            : {}),
        ...(studyModelClass === 'fh-t' ? { studyModelClass } : {}),
        ...(studyQuadratureNodes !== undefined ? { studyQuadratureNodes } : {}),
    };
}
function cellDefsFor(config) {
    const defs = [];
    for (const indicator of config.indicators) {
        defs.push({ cellId: indicator.id, indicatorId: indicator.id });
        for (const spec of config.subgroups) {
            for (const level of spec.levels) {
                defs.push({
                    cellId: `${indicator.id}|${spec.variable}|${String(level.value)}`,
                    indicatorId: indicator.id,
                    subgroupVariable: spec.variable,
                    subgroupLevel: level.value,
                });
            }
        }
    }
    return defs;
}
export async function runAnalysisImpl(input, config, onProgress) {
    const startedAt = new Date();
    const manifest = {
        runId: `sae-${startedAt.getTime().toString(36)}`,
        engineVersion: ENGINE_VERSION,
        startedAt: startedAt.toISOString(),
        seed: config.uncertainty.seed,
    };
    const cellDefs = cellDefsFor(config);
    const totalSteps = cellDefs.length + 2;
    const report = (event) => {
        if (onProgress)
            onProgress(event);
    };
    // ---- Direct estimates, design effects, transformation (9.3-9.5). -
    const directEstimates = computeDirectEstimatesImpl(input.respondents, {
        indicators: config.indicators.map((i) => i.id),
        subgroups: config.subgroups,
        lonelyPsu: config.design.lonelyPsu,
        fpc: config.design.fpc,
        psuPopulationCounts: config.design.psuPopulationCounts,
        // Candidate W (Decision Log Addendum 50 Item 1): the per-domain
        // stepwise FPC variances exist only for the 'within-area'
        // design-effect method, whose transform stage reads FPC_d from
        // them. Workstream F-1 (Addendum 56) reads the same per-domain
        // variances for its 'area-direct' blend. Every other method
        // runs the shipped statewide-only computation, bit-identically.
        areaFpcVariance: config.model.deff.method === 'within-area' ||
            config.model.deff.method === 'area-direct',
        confidence: config.uncertainty.confidence,
    });
    report({ stage: 'direct', completed: 1, total: totalSteps });
    const cellDesignEffects = smoothDesignEffectsImpl(directEstimates, config.model.deff);
    const deffByCell = new Map(cellDesignEffects.map((c) => [c.cellId, c]));
    const transformed = transformDomainsImpl(directEstimates, cellDesignEffects, {
        transform: config.model.transform,
        meanBiasCorrection: config.model.transformBiasCorrection ?? false,
        // Study-only variant (Tier 2 Item 1): the exact-expectation
        // correction term in place of the shipped delta plug-in.
        // Absent (every app run), the transform applies the plug-in,
        // unchanged.
        meanBiasCorrectionForm: config.model.studyTransformBiasCorrectionForm,
        // Workstream F-1 (Addendum 56): the 'area-direct' shrinkage
        // constant, threaded from the model config; absent = the
        // engine constant fixed at the F-1 measurement freeze.
        areaDirectK: config.model.deff.areaDirectK,
    });
    report({ stage: 'design-effects', completed: 2, total: totalSteps });
    // ---- Candidate areas and covariate assembly (9.14). --------------
    const candidateAreas = [];
    const seenArea = new Set();
    const addArea = (a) => {
        if (!seenArea.has(a)) {
            seenArea.add(a);
            candidateAreas.push(a);
        }
    };
    for (const r of input.respondents)
        addArea(r.areaId);
    const covRowByArea = new Map();
    for (const row of input.areaCovariates ?? []) {
        covRowByArea.set(row.areaId, row);
        addArea(row.areaId);
    }
    for (const key of Object.keys(input.populationCounts ?? {})) {
        addArea(key);
    }
    const globalIssues = [];
    // Auto-mode log transformation of strongly skewed, strictly
    // positive candidates (brief 9.14), applied once per run at
    // assembly; recorded so the choice is visible in the QC output.
    const logTransformed = new Set();
    const rawCovariateValue = (areaId, name) => {
        const row = covRowByArea.get(areaId);
        const v = row?.values[name];
        return v === undefined ? Number.NaN : v;
    };
    for (const name of config.covariates.candidates) {
        const values = candidateAreas
            .map((a) => rawCovariateValue(a, name))
            .filter((v) => Number.isFinite(v));
        if (values.length >= 3 &&
            values.every((v) => v > 0) &&
            Math.abs(skewnessOf(values)) > 2) {
            logTransformed.add(name);
            globalIssues.push({
                code: 'COV_TRANSFORMED',
                severity: 'info',
                message: `Covariate "${name}" was log-transformed before modeling ` +
                    'because it is strongly skewed (brief 9.14, Auto mode).',
            });
        }
    }
    const covariateValue = (areaId, name) => {
        const v = rawCovariateValue(areaId, name);
        if (!Number.isFinite(v))
            return v;
        return logTransformed.has(name) ? Math.log(v) : v;
    };
    /** Effective population count for an area: explicit input wins. */
    const populationCountOf = (areaId) => {
        const explicit = input.populationCounts?.[areaId];
        if (explicit !== undefined && Number.isFinite(explicit) && explicit > 0) {
            return explicit;
        }
        const fromRow = covRowByArea.get(areaId)?.populationCount;
        if (fromRow !== undefined && Number.isFinite(fromRow) && fromRow > 0) {
            return fromRow;
        }
        return undefined;
    };
    const directByCellArea = new Map();
    for (const e of directEstimates) {
        directByCellArea.set(domainKeyOf(e.domain), e);
    }
    const transformedByCellArea = new Map();
    for (const t of transformed) {
        transformedByCellArea.set(domainKeyOf(t.domain), t);
    }
    const statewideByCell = new Map();
    for (const e of directEstimates) {
        if (e.domain.areaId === STATEWIDE_AREA_ID)
            statewideByCell.set(e.domain.cellId, e);
    }
    const selectionConfigFull = {
        ...config.covariates.selection,
        locked: config.covariates.locked,
        excluded: config.covariates.excluded,
    };
    const lockedSet = new Set(config.covariates.locked);
    /** Selection cache for scope 'overall', keyed by indicator id. */
    const overallSelections = new Map();
    /**
     * Disclosure-record cache for scope 'overall' (Tier 3 verdict
     * fix 1): the selecting cell's offered set, criterion, and base
     * drops, reused by the indicator's other cells, which then add
     * their own per-cell drops (trims, singular-design drops).
     */
    const overallSelectionRecords = new Map();
    const domains = [];
    const disclosureInputs = [];
    const cells = [];
    const recordDomain = (partial, disclosure) => {
        domains.push({ ...partial, status: 'publish' });
        disclosureInputs.push(disclosure);
    };
    /**
     * The proportion standard's width-test fields for a domain
     * reported from its direct estimate (Tier 2 Item 6): the width
     * of the estimate's Korn-Graubard interval (brief 9.3) at the
     * run's confidence level, and that width relative to the
     * estimate. The standard (Parker et al. 2017) defines its width
     * tests on this interval; the reported interval stays the brief
     * 9.3 interval. Supplied for every direct-reported domain,
     * whatever the active preset, exactly as the effective sample
     * size and degrees of freedom are; only the proportion-standard
     * preset reads them.
     */
    const kgAlpha = 1 - config.uncertainty.confidence;
    const standardWidthFields = (direct) => {
        const kg = kornGraubardBounds(direct.estimate, direct.variance, direct.n, direct.degreesOfFreedom, kgAlpha);
        const w = kg.upper - kg.lower;
        return {
            standardIntervalWidth: w,
            standardRelativeIntervalWidth: direct.estimate > 0
                ? w / direct.estimate
                : w > 0
                    ? Number.POSITIVE_INFINITY
                    : 0,
        };
    };
    // ---- Per-cell pipeline. -------------------------------------------
    for (let ci = 0; ci < cellDefs.length; ci++) {
        const def = cellDefs[ci];
        const cellIssues = [];
        const cellDomain = (areaId) => ({
            cellId: def.cellId,
            indicatorId: def.indicatorId,
            ...(def.subgroupVariable !== undefined
                ? { subgroupVariable: def.subgroupVariable, subgroupLevel: def.subgroupLevel }
                : {}),
            areaId,
        });
        const presentAreas = candidateAreas.filter((a) => directByCellArea.has(`${def.cellId}${a}`));
        const presentSet = new Set(presentAreas);
        const missingAreas = candidateAreas.filter((a) => !presentSet.has(a));
        if (missingAreas.length > 0) {
            cellIssues.push({
                code: 'MISSING_DIRECT_ESTIMATE',
                severity: 'warning',
                cellId: def.cellId,
                message: 'No direct estimate could be computed in this cell for: ' +
                    `${missingAreas.join(', ')}. These areas are excluded from ` +
                    'the model for this cell (decision S4).',
            });
        }
        const transformedRows = presentAreas
            .map((a) => transformedByCellArea.get(`${def.cellId}${a}`))
            .filter((t) => t !== undefined);
        const fittableAreas = transformedRows
            .filter((t) => t.effectiveSampleSize > 0)
            .map((t) => t.domain.areaId);
        const fittableSet = new Set(fittableAreas);
        const statewide = statewideByCell.get(def.cellId);
        const baseCell = {
            cellId: def.cellId,
            indicatorId: def.indicatorId,
            ...(def.subgroupVariable !== undefined
                ? { subgroupVariable: def.subgroupVariable, subgroupLevel: def.subgroupLevel }
                : {}),
            status: 'insufficient-areas',
            designEffect: deffByCell.get(def.cellId)?.designEffect,
            selectedCovariates: [],
            transform: config.model.transform,
            benchmarkApplied: false,
            issues: cellIssues,
        };
        /** Direct-only reporting for every present area (insufficient cells and fit failures). */
        const reportDirectOnly = () => {
            for (const areaId of presentAreas) {
                const direct = directByCellArea.get(`${def.cellId}${areaId}`);
                const t = transformedByCellArea.get(`${def.cellId}${areaId}`);
                const width = direct.interval.upper - direct.interval.lower;
                const relWidth = direct.estimate > 0
                    ? width / direct.estimate
                    : width > 0
                        ? Number.POSITIVE_INFINITY
                        : 0;
                const cv = direct.estimate > 0
                    ? direct.standardError / direct.estimate
                    : direct.standardError > 0
                        ? Number.POSITIVE_INFINITY
                        : 0;
                const reliability = {
                    n: direct.n,
                    effectiveSampleSize: t?.effectiveSampleSize ?? 0,
                    degreesOfFreedom: direct.degreesOfFreedom,
                    rmse: direct.standardError,
                    cv,
                    intervalWidth: width,
                    relativeIntervalWidth: relWidth,
                };
                const W = populationCountOf(areaId) ?? Math.round(direct.weightedTotal);
                const weightedYes = Math.round(direct.estimate * W);
                recordDomain({
                    domain: cellDomain(areaId),
                    direct,
                    estimateType: 'Direct',
                    reliability,
                    complement: {
                        estimate: 1 - direct.estimate,
                        lower: 1 - direct.interval.upper,
                        upper: 1 - direct.interval.lower,
                        countYes: direct.yesCount,
                        countNo: direct.n - direct.yesCount,
                        weightedYes,
                        weightedNo: W - weightedYes,
                    },
                }, {
                    domain: cellDomain(areaId),
                    n: direct.n,
                    cv,
                    intervalWidth: width,
                    relativeIntervalWidth: relWidth,
                    psuCount: direct.psuCount,
                    effectiveSampleSize: t?.effectiveSampleSize ?? 0,
                    degreesOfFreedom: direct.degreesOfFreedom,
                    eventCount: direct.yesCount,
                    ...standardWidthFields(direct),
                });
            }
        };
        if (fittableAreas.length < config.model.minAreas) {
            cellIssues.push({
                code: 'FEW_AREAS',
                severity: 'warning',
                cellId: def.cellId,
                message: `Only ${fittableAreas.length} areas have respondents in this ` +
                    `cell; ${config.model.minAreas} are required to fit the model ` +
                    '(brief 9.15). Direct estimates are reported instead.',
            });
            reportDirectOnly();
            cells.push(baseCell);
            report({ stage: 'cell', completed: 3 + ci, total: totalSteps, cellId: def.cellId });
            continue;
        }
        // ---- Study-only GVF first-pass psi (Tier 2 Item 7) --------------
        // psi^(1) of the two-pass GVF (see the study block at the fit
        // below): deff * q (1 - q) / n per area with q the
        // half-count-clamped direct. It equals the shipped psi at
        // every interior direct and exists at boundary directs, where
        // the shipped linear psi is 0 — and 0 is not merely a GVF
        // problem: covariate selection validates psi > 0, so on the
        // linear scale a single boundary direct makes the shipped
        // pipeline throw for the whole cell before any fit. Under
        // the study switch the selection therefore reads psi^(1) (in
        // both arms, identically — psi^(1) carries no clamp choice),
        // which is what "running the specified two-pass GVF through
        // the pipeline" means for the stages upstream of the fit.
        // Absent the switch this map is undefined and every read
        // below falls back to the shipped psi, unchanged.
        const gvfActive = config.model.studyGvfClamp !== undefined &&
            config.model.transform === 'linear';
        const gvfHalfCount = (n) => [
            1 / (2 * n),
            1 - 1 / (2 * n),
        ];
        const gvfPsi1 = gvfActive
            ? new Map(fittableAreas.map((a) => {
                const t = transformedByCellArea.get(`${def.cellId}${a}`);
                const nA = Math.max(1, t.n);
                const [lo, hi] = gvfHalfCount(nA);
                const q = Math.min(Math.max(t.thetaHat, lo), hi);
                return [a, (t.designEffect * q * (1 - q)) / nA];
            }))
            : undefined;
        // Candidate V (Path to A, Part 2) is active only behind its
        // study switch on the 'within-area' deff method. On the linear
        // scale its first pass faces exactly the boundary problem the
        // GVF block above documents: W's linear psi is 0 at a boundary
        // direct, and selection (psi > 0) and the pass-1 fit would both
        // throw before pass 2 could repair it — the failure guide
        // Section 2.3 says V removes. The candidate therefore reads, for
        // the stages upstream of pass 2 on the linear scale only, the
        // same psi^(1) form: deff * q (1 - q) / n with q the
        // half-count-clamped direct. psi^(1) equals W's psi at every
        // interior direct, so pass 1 is W's fit wherever W's fit exists;
        // it differs only at boundary directs, where W's psi is 0 and
        // no W fit exists. On the arcsine scale psi is never 0 and this
        // map is undefined: pass 1 reads W's psi directly.
        const candidateVActive = config.model.studyPsiCandidateV === true &&
            config.model.deff.method === 'within-area';
        const candidateVPsi1 = candidateVActive && config.model.transform === 'linear'
            ? new Map(fittableAreas.map((a) => {
                const t = transformedByCellArea.get(`${def.cellId}${a}`);
                const nA = Math.max(1, t.n);
                const [lo, hi] = gvfHalfCount(nA);
                const q = Math.min(Math.max(t.thetaHat, lo), hi);
                return [a, (t.designEffect * q * (1 - q)) / nA];
            }))
            : undefined;
        // Candidate L (Registration R-10, Addendum 98) is active only
        // behind its study switch on the 'state' deff method — the
        // shipped P2 basis, whose design effect L leaves unchanged. On
        // the linear scale its first pass faces exactly the boundary
        // problem candidate V's block above documents: P2's linear psi
        // is 0 at a boundary direct, and selection (psi > 0) and the
        // pass-1 fit would both throw before pass 2 could repair it.
        // The candidate therefore reads, for the stages upstream of
        // pass 2 on the linear scale only, the same psi^(1) form:
        // deff * q (1 - q) / n with q the half-count-clamped direct and
        // deff the transform's per-domain factor (under 'state', the
        // cell's smoothed statewide design effect). psi^(1) equals
        // P2's psi at every interior direct, so pass 1 is P2's fit
        // wherever P2's fit exists; it differs only at boundary
        // directs, where P2's psi is 0 and no P2 fit exists. On the
        // arcsine scale psi is never 0 and this map is undefined:
        // pass 1 reads P2's psi directly.
        const candidateLActive = config.model.studyPsiCandidateL === true &&
            config.model.deff.method === 'state';
        const candidateLPsi1 = candidateLActive && config.model.transform === 'linear'
            ? new Map(fittableAreas.map((a) => {
                const t = transformedByCellArea.get(`${def.cellId}${a}`);
                const nA = Math.max(1, t.n);
                const [lo, hi] = gvfHalfCount(nA);
                const q = Math.min(Math.max(t.thetaHat, lo), hi);
                return [a, (t.designEffect * q * (1 - q)) / nA];
            }))
            : undefined;
        // Candidate V2 (combined gate R-6, Addendum 89) is active only
        // behind its study switch on the 'within-area' deff method. Its
        // pooled variance components and per-area design effects come
        // from the cell's microdata (v2components.ts); when they are
        // undefined for the cell, V2 does not run for it and W's psi
        // stands — candidate V's fallback rule. On the linear scale the
        // stages upstream of pass 2 read the same psi^(1) form as V's
        // block above, with deff_d in place of the area's design
        // effect; on the arcsine scale pass 1 reads W's psi directly,
        // as V does.
        const candidateV2Active = config.model.studyPsiCandidateV2 === true &&
            config.model.deff.method === 'within-area';
        const candidateV2Components = candidateV2Active
            ? computeV2Components({
                respondents: input.respondents,
                indicatorId: def.indicatorId,
                subgroupVariable: def.subgroupVariable,
                subgroupLevel: def.subgroupLevel,
                cellDirectEstimates: directEstimates.filter((e) => e.domain.cellId === def.cellId),
                psuPopulationCounts: config.design.psuPopulationCounts,
            })
            : undefined;
        const candidateV2Psi1 = candidateV2Active &&
            candidateV2Components !== undefined &&
            config.model.transform === 'linear'
            ? new Map(fittableAreas.flatMap((a) => {
                const deffD = candidateV2Components.deffByArea.get(a);
                if (deffD === undefined)
                    return [];
                const t = transformedByCellArea.get(`${def.cellId}${a}`);
                const nA = Math.max(1, t.n);
                const [lo, hi] = gvfHalfCount(nA);
                const q = Math.min(Math.max(t.thetaHat, lo), hi);
                return [[a, (deffD * q * (1 - q)) / nA]];
            }))
            : undefined;
        // ---- Covariate selection (brief 9.14). --------------------------
        const thetaHat = fittableAreas.map((a) => transformedByCellArea.get(`${def.cellId}${a}`).thetaHat);
        const psi = fittableAreas.map((a) => candidateV2Psi1?.get(a) ??
            candidateVPsi1?.get(a) ??
            candidateLPsi1?.get(a) ??
            gvfPsi1?.get(a) ??
            transformedByCellArea.get(`${def.cellId}${a}`).psi);
        // ---- Selection disclosure record (Tier 3 verdict fix 1). -----
        // Assembled alongside the selection itself: what was offered,
        // what the search dropped and by what rule; the per-cell trims
        // and fit-time drops append to lateDrops below. Pure disclosure —
        // nothing here feeds back into fitting.
        let selectionOffered;
        let selectionCriterion = config.covariates.selection.criterion;
        let selectionBaseDrops = [];
        const lateDrops = [];
        const selectionRecord = (finalSelected) => selectionOffered === undefined
            ? undefined
            : {
                offered: selectionOffered,
                kept: finalSelected,
                dropped: [...selectionBaseDrops, ...lateDrops].filter((d) => !finalSelected.includes(d.name)),
                criterion: selectionCriterion,
            };
        let selected;
        if (config.covariates.selection.scope === 'overall' && def.subgroupVariable !== undefined) {
            selected = overallSelections.get(def.indicatorId) ?? [];
            const cached = overallSelectionRecords.get(def.indicatorId);
            if (cached !== undefined) {
                selectionOffered = cached.offered;
                selectionCriterion = cached.criterion;
                selectionBaseDrops = [...cached.dropped];
            }
        }
        else {
            const candidates = {};
            for (const name of config.covariates.candidates) {
                candidates[name] = fittableAreas.map((a) => covariateValue(a, name));
            }
            const result = selectCovariatesImpl({
                thetaHat,
                psi,
                candidates,
                config: selectionConfigFull,
            });
            selected = result.selected;
            // Disclosure record for this selection: every offered covariate
            // the search did not keep is accounted to exactly one rule —
            // the user's own exclusion, a named screen, or the search
            // itself (Tier 3 verdict fix 1).
            selectionOffered = [...config.covariates.candidates];
            selectionCriterion = result.criterion;
            const screenReasonByName = new Map();
            for (const step of result.path) {
                if (step.variable === undefined ||
                    !Number.isNaN(step.criterionValue) ||
                    step.reason === undefined) {
                    continue;
                }
                screenReasonByName.set(step.variable, step.reason);
            }
            selectionBaseDrops = [];
            for (const name of config.covariates.candidates) {
                if (result.selected.includes(name))
                    continue;
                if (config.covariates.excluded.includes(name)) {
                    selectionBaseDrops.push({ name, rule: 'user-excluded' });
                }
                else {
                    selectionBaseDrops.push({
                        name,
                        rule: screenReasonByName.get(name) ?? 'not-selected',
                    });
                }
            }
            for (const step of result.path) {
                if (step.variable === undefined || !Number.isNaN(step.criterionValue))
                    continue;
                const name = step.variable;
                const reason = step.reason === 'missing-values'
                    ? 'it has missing values in this cell'
                    : step.reason === 'no-variation'
                        ? 'it has no variation across this cell\u2019s areas'
                        : step.reason === 'collinearity'
                            ? 'it failed the collinearity screen (correlated with another covariate)'
                            : step.reason === 'vif'
                                ? 'it failed the collinearity screen (variance inflation factor above the threshold)'
                                : 'it failed the screening checks (no variation or collinearity)';
                cellIssues.push({
                    code: 'COV_DROPPED',
                    severity: 'warning',
                    cellId: def.cellId,
                    message: `Covariate "${name}" was dropped because ${reason} (brief 9.14).`,
                });
            }
            if (config.covariates.selection.scope === 'overall') {
                overallSelections.set(def.indicatorId, selected);
                overallSelectionRecords.set(def.indicatorId, {
                    offered: selectionOffered,
                    criterion: selectionCriterion,
                    dropped: [...selectionBaseDrops],
                });
            }
        }
        // Trim a reused (overall-scope) selection to this cell's size
        // limit, most recently added first; drop covariates unusable in
        // this cell (missing values among its areas).
        {
            const pMax = maxCovariatesFor(fittableAreas.length, config.covariates.selection);
            const trimmed = [...selected];
            let blocked = false;
            for (let i = trimmed.length - 1; i >= 0; i--) {
                const name = trimmed[i];
                const unusable = fittableAreas.some((a) => !Number.isFinite(covariateValue(a, name)));
                if (unusable) {
                    if (lockedSet.has(name)) {
                        blocked = true;
                    }
                    else {
                        trimmed.splice(i, 1);
                        lateDrops.push({ name, rule: 'missing-in-cell' });
                        cellIssues.push({
                            code: 'COV_DROPPED',
                            severity: 'warning',
                            cellId: def.cellId,
                            message: `Covariate "${name}" was dropped for this cell because ` +
                                'it has missing values among its areas (brief 9.14).',
                        });
                    }
                }
            }
            while (trimmed.length > pMax) {
                const droppedIdx = trimmed.map((n, i) => ({ n, i })).filter((x) => !lockedSet.has(x.n)).pop();
                if (droppedIdx === undefined)
                    break;
                trimmed.splice(droppedIdx.i, 1);
                lateDrops.push({ name: droppedIdx.n, rule: 'size-limit' });
            }
            // Residual-degrees-of-freedom guard (brief 9.15 fit conditions).
            while (trimmed.length > 0 &&
                fittableAreas.length - (1 + trimmed.length) < 5) {
                const droppedIdx = trimmed.map((n, i) => ({ n, i })).filter((x) => !lockedSet.has(x.n)).pop();
                if (droppedIdx === undefined)
                    break;
                trimmed.splice(droppedIdx.i, 1);
                lateDrops.push({ name: droppedIdx.n, rule: 'degrees-of-freedom' });
            }
            selected = trimmed;
            if (blocked) {
                cellIssues.push({
                    code: 'COV_DROPPED',
                    severity: 'blocking',
                    cellId: def.cellId,
                    message: 'A locked covariate has missing values in this cell, so the ' +
                        'model cannot be fitted. Direct estimates are reported.',
                });
                reportDirectOnly();
                const blockedRecord = selectionRecord(selected);
                cells.push({
                    ...baseCell,
                    selectedCovariates: selected,
                    ...(blockedRecord !== undefined ? { selection: blockedRecord } : {}),
                });
                report({ stage: 'cell', completed: 3 + ci, total: totalSteps, cellId: def.cellId });
                continue;
            }
        }
        // ---- Study-only two-pass GVF (Tier 2 Item 7) -------------------
        // The two-pass generalized variance function specified in
        // transform.ts (directive C-3, OPEN_QUESTIONS Q8) exists in the
        // shipped engine only as its one-pass analogue; this block runs
        // the specified second pass when ModelConfig.studyGvfClamp is
        // set, so the clamp question can be measured before any
        // adoption. Absent the switch, nothing here executes and the
        // shipped psi is used unchanged. Linear transform only (the
        // arcsine psi, 1 / (4 n_eff), has no GVF form). The first pass
        // refits with psi^(1) (computed at the selection stage above,
        // which also reads it under the switch — psi^(2) needs a fit,
        // which needs the selection, so the selection cannot read
        // psi^(2)). The second-pass psi replaces each area's psi with
        // deff * ptilde_d (1 - ptilde_d) / n_d, ptilde the first-pass
        // synthetic prediction clamped per the arm: 'epsilon' is the
        // clamp as originally specified, [1e-6, 1 - 1e-6];
        // 'half-count' is [1/(2 n_d), 1 - 1/(2 n_d)] (Addendum 16's
        // clamp; the specification carries it since the Item 7
        // measurement, 2026-10-20). Exactly one
        // refit, as specified. If the first pass cannot be fitted, the
        // cell keeps the shipped psi (the study GVF simply does not
        // run for it). The bootstrap reads the fit domains, so it
        // inherits psi^(2) exactly as it inherits psi^(1) in shipped
        // runs.
        let gvfPsi;
        if (gvfActive && gvfPsi1 !== undefined && fittableAreas.length > 0) {
            const clampArm = config.model.studyGvfClamp;
            try {
                const firstPass = fittableAreas.map((a) => {
                    const t = transformedByCellArea.get(`${def.cellId}${a}`);
                    return {
                        areaId: a,
                        thetaHat: t.thetaHat,
                        psi: gvfPsi1.get(a),
                        covariates: [1, ...selected.map((n) => covariateValue(a, n))],
                    };
                });
                const fit1 = fitFayHerriotImpl(firstPass, fitConfigFor(config));
                const psi2 = new Map();
                let usable = true;
                for (const a of fittableAreas) {
                    const t = transformedByCellArea.get(`${def.cellId}${a}`);
                    const synth = fit1.areas.find((x) => x.areaId === a)?.syntheticTheta;
                    if (synth === undefined || !Number.isFinite(synth)) {
                        usable = false;
                        break;
                    }
                    const nA = Math.max(1, t.n);
                    const [lo, hi] = clampArm === 'half-count' ? gvfHalfCount(nA) : [1e-6, 1 - 1e-6];
                    const ptilde = Math.min(Math.max(synth, lo), hi);
                    psi2.set(a, (t.designEffect * ptilde * (1 - ptilde)) / nA);
                }
                if (usable)
                    gvfPsi = psi2;
            }
            catch {
                // First pass not fittable for this cell: the study GVF
                // does not run and the shipped psi stands (documented
                // above).
                gvfPsi = undefined;
            }
        }
        // ---- Study-only candidate V two-pass psi (Path to A, Part 2) --
        // Candidate V (guide Sections 2.2 and 2.3) runs only when
        // ModelConfig.studyPsiCandidateV is set AND the design-effect
        // method is 'within-area': candidate W's psi, already produced
        // by the transform stage under that method, is the first pass.
        // Pass 1 fits the shipped model with W's psi (the domains below
        // at their transformed psi). The smoothed prevalence is the
        // pass-1 fit's SYNTHETIC prediction (AreaFit.syntheticTheta),
        // never the EBLUP: on the arcsine scale ptilde_d =
        // sin^2(clamp(syntheticTheta, 0, pi/2)), then ptilde_d is
        // clamped to [0.01, 0.99]; on the linear scale ptilde_d =
        // clamp(syntheticTheta, 0.01, 0.99). Pass 2 psi: on the arcsine
        // scale psi_d = exactBinomialArcsineVariance(nEff_d, ptilde_d)
        // with nEff_d the domain's effectiveSampleSize from the
        // 'within-area' transform (= n_d / (deff_w x FPC_d)), floored
        // at 2 per guide Section 2.2; on the linear scale psi_d =
        // designEffect_d * ptilde_d (1 - ptilde_d) / n_d with
        // designEffect_d the transform's per-domain factor (deff_w x
        // FPC_d, TransformedDomain.designEffect). Because ptilde_d is
        // clamped inside (0, 1), psi is strictly positive by
        // construction; that is asserted per domain below. Exactly two
        // passes — the pass-2 fit's own synthetic predictions are never
        // fed back. If the pass-1 fit cannot be fitted, the cell keeps
        // W's psi (the shipped psi under 'within-area'), mirroring the
        // GVF block's documented fallback above. Absent the switch, or
        // under any other deff method, nothing here executes. The main
        // fit reads pass-2 psi through buildDomains exactly as it reads
        // the GVF map, so the MSE, the parametric bootstrap (which reads
        // the fit domains and therefore holds psi fixed at this
        // observed-data value — pass 1 is not re-run inside replicates),
        // and benchmarking all inherit it unchanged.
        let candidateVPsi;
        if (candidateVActive && fittableAreas.length > 0) {
            try {
                const firstPass = fittableAreas.map((a) => {
                    const t = transformedByCellArea.get(`${def.cellId}${a}`);
                    return {
                        areaId: a,
                        thetaHat: t.thetaHat,
                        // W's psi; on the linear scale the psi^(1) map above,
                        // which equals W's psi at every interior direct.
                        psi: candidateVPsi1?.get(a) ?? t.psi,
                        covariates: [1, ...selected.map((n) => covariateValue(a, n))],
                    };
                });
                const fit1 = fitFayHerriotImpl(firstPass, fitConfigFor(config));
                const psiV = new Map();
                let usable = true;
                for (const a of fittableAreas) {
                    const t = transformedByCellArea.get(`${def.cellId}${a}`);
                    const synth = fit1.areas.find((x) => x.areaId === a)?.syntheticTheta;
                    if (synth === undefined || !Number.isFinite(synth)) {
                        usable = false;
                        break;
                    }
                    let psi;
                    if (config.model.transform === 'arcsin') {
                        const mapped = Math.sin(clamp(synth, 0, Math.PI / 2)) ** 2;
                        const ptilde = clamp(mapped, 0.01, 0.99);
                        psi = exactBinomialArcsineVariance(Math.max(t.effectiveSampleSize, 2), ptilde);
                    }
                    else {
                        const ptilde = clamp(synth, 0.01, 0.99);
                        psi = (t.designEffect * ptilde * (1 - ptilde)) / t.n;
                    }
                    if (!Number.isFinite(psi) || !(psi > 0)) {
                        // Assert of the construction: psi must be > 0 by
                        // construction (ptilde clamped inside (0, 1)).
                        throw new Error(`candidate V: pass-2 psi must be positive and finite for area "${a}"; received ${psi}.`);
                    }
                    psiV.set(a, psi);
                }
                if (usable)
                    candidateVPsi = psiV;
            }
            catch {
                // Pass 1 not fittable for this cell (or the psi assert
                // above fired): candidate V does not run and W's psi
                // stands (documented above).
                candidateVPsi = undefined;
            }
        }
        // ---- Study-only candidate L two-pass psi (Registration R-10) --
        // Candidate L (the reviewer's specification, recorded at
        // Addendum 98) runs only when ModelConfig.studyPsiCandidateL is
        // set AND the design-effect method is 'state': the shipped P2
        // basis, whose psi the transform stage has already produced, is
        // the first pass — P2's design-effect basis is unchanged, and
        // only the bridge is replaced. Pass 1 fits the shipped model
        // with P2's psi (the domains below at their transformed psi).
        // The smoothed prevalence is the pass-1 fit's SYNTHETIC
        // prediction (AreaFit.syntheticTheta), never the EBLUP, mapped
        // and clamped exactly as candidate V maps it: on the arcsine
        // scale ptilde_d = sin^2(clamp(syntheticTheta, 0, pi/2)), then
        // clamped to [0.01, 0.99]; on the linear scale ptilde_d =
        // clamp(syntheticTheta, 0.01, 0.99). Pass 2 psi: on the arcsine
        // scale psi_d = exactBinomialArcsineVariance(nEff_d, ptilde_d)
        // — the identical function candidate V calls, in the identical
        // argument form — with nEff_d the domain's effectiveSampleSize
        // from the 'state' transform (= n_d / deff_P2, P2's own
        // effective sample size), floored at 2 as in V's construction;
        // on the linear scale psi_d = designEffect_d * ptilde_d
        // (1 - ptilde_d) / n_d with designEffect_d the transform's
        // per-domain factor (under 'state', deff_P2 as recorded).
        // Because ptilde_d is clamped inside (0, 1), psi is strictly
        // positive by construction; that is asserted per domain below.
        // Exactly two passes — the pass-2 fit's own synthetic
        // predictions are never fed back. If the pass-1 fit cannot be
        // fitted, the cell keeps P2's psi, mirroring candidate V's
        // documented fallback. Absent the switch, or under any other
        // deff method, nothing here executes. The main fit reads pass-2
        // psi through buildDomains exactly as it reads candidate V's
        // map, so the MSE, the parametric bootstrap (which reads the
        // fit domains and therefore holds psi fixed at this
        // observed-data value — pass 1 is not re-run inside replicates),
        // and benchmarking all inherit it unchanged. psi feeds the fit
        // through gamma as well as the MSE, so L can move estimates
        // slightly; the R-10 gate scores that movement as it lands.
        let candidateLPsi;
        if (candidateLActive && fittableAreas.length > 0) {
            try {
                const firstPass = fittableAreas.map((a) => {
                    const t = transformedByCellArea.get(`${def.cellId}${a}`);
                    return {
                        areaId: a,
                        thetaHat: t.thetaHat,
                        // P2's psi; on the linear scale the psi^(1) map above,
                        // which equals P2's psi at every interior direct.
                        psi: candidateLPsi1?.get(a) ?? t.psi,
                        covariates: [1, ...selected.map((n) => covariateValue(a, n))],
                    };
                });
                const fit1 = fitFayHerriotImpl(firstPass, fitConfigFor(config));
                const psiL = new Map();
                let usable = true;
                for (const a of fittableAreas) {
                    const t = transformedByCellArea.get(`${def.cellId}${a}`);
                    const synth = fit1.areas.find((x) => x.areaId === a)?.syntheticTheta;
                    if (synth === undefined || !Number.isFinite(synth)) {
                        usable = false;
                        break;
                    }
                    let psi;
                    if (config.model.transform === 'arcsin') {
                        const mapped = Math.sin(clamp(synth, 0, Math.PI / 2)) ** 2;
                        const ptilde = clamp(mapped, 0.01, 0.99);
                        psi = exactBinomialArcsineVariance(Math.max(t.effectiveSampleSize, 2), ptilde);
                    }
                    else {
                        const ptilde = clamp(synth, 0.01, 0.99);
                        psi = (t.designEffect * ptilde * (1 - ptilde)) / t.n;
                    }
                    if (!Number.isFinite(psi) || !(psi > 0)) {
                        // Assert of the construction: psi must be > 0 by
                        // construction (ptilde clamped inside (0, 1)).
                        throw new Error(`candidate L: pass-2 psi must be positive and finite for area "${a}"; received ${psi}.`);
                    }
                    psiL.set(a, psi);
                }
                if (usable)
                    candidateLPsi = psiL;
            }
            catch {
                // Pass 1 not fittable for this cell (or the psi assert
                // above fired): candidate L does not run and P2's psi
                // stands (documented above).
                candidateLPsi = undefined;
            }
        }
        // Candidate V2 (combined gate R-6, Addendum 89): candidate V's
        // two-pass form with the pass-2 design effect replaced by the
        // registered two-stage deff_d (v2components.ts). Pass 1 is the
        // shipped model fitted at W's psi; pass 2 reads the pass-1
        // SYNTHETIC predictions, mapped and clamped exactly as V maps
        // them. Exactly two passes; the bootstrap holds the pass-2 psi
        // fixed (it reads the final domains' psi). When the components
        // are undefined for the cell, or the first pass cannot be
        // fitted, W's psi stands.
        let candidateV2Psi;
        if (candidateV2Active &&
            candidateV2Components !== undefined &&
            fittableAreas.length > 0) {
            try {
                const firstPass = fittableAreas.map((a) => {
                    const t = transformedByCellArea.get(`${def.cellId}${a}`);
                    return {
                        areaId: a,
                        thetaHat: t.thetaHat,
                        // W's psi; on the linear scale the psi^(1) map above,
                        // which equals W's psi at every interior direct.
                        psi: candidateV2Psi1?.get(a) ?? t.psi,
                        covariates: [1, ...selected.map((n) => covariateValue(a, n))],
                    };
                });
                const fit1 = fitFayHerriotImpl(firstPass, fitConfigFor(config));
                const psiV2 = new Map();
                let usable = true;
                for (const a of fittableAreas) {
                    const t = transformedByCellArea.get(`${def.cellId}${a}`);
                    const deffD = candidateV2Components.deffByArea.get(a);
                    if (deffD === undefined || !(deffD > 0)) {
                        usable = false;
                        break;
                    }
                    const synth = fit1.areas.find((x) => x.areaId === a)?.syntheticTheta;
                    if (synth === undefined || !Number.isFinite(synth)) {
                        usable = false;
                        break;
                    }
                    let psi2;
                    if (config.model.transform === 'arcsin') {
                        const mapped = Math.sin(clamp(synth, 0, Math.PI / 2)) ** 2;
                        const ptilde = clamp(mapped, 0.01, 0.99);
                        psi2 = exactBinomialArcsineVariance(Math.max(t.n / deffD, 2), ptilde);
                    }
                    else {
                        const ptilde = clamp(synth, 0.01, 0.99);
                        psi2 = (deffD * ptilde * (1 - ptilde)) / t.n;
                    }
                    if (!Number.isFinite(psi2) || !(psi2 > 0)) {
                        // Assert of the construction, mirroring candidate V:
                        // psi must be > 0 by construction (ptilde clamped
                        // inside (0, 1), deff_d > 0 checked above).
                        throw new Error(`candidate V2: pass-2 psi must be positive and finite for area "${a}"; received ${psi2}.`);
                    }
                    psiV2.set(a, psi2);
                }
                if (usable)
                    candidateV2Psi = psiV2;
            }
            catch {
                // Pass 1 not fittable for this cell (or the psi assert
                // above fired): candidate V2 does not run and W's psi
                // stands (candidate V's rule).
                candidateV2Psi = undefined;
            }
        }
        // ---- Fit, with the singular-design dropping fallback. -----------
        const buildDomains = (covs) => {
            const domainsIn = fittableAreas.map((a) => {
                const t = transformedByCellArea.get(`${def.cellId}${a}`);
                return {
                    areaId: a,
                    thetaHat: t.thetaHat,
                    psi: candidateV2Psi?.get(a) ??
                        candidateVPsi?.get(a) ??
                        candidateLPsi?.get(a) ??
                        gvfPsi?.get(a) ??
                        t.psi,
                    covariates: [1, ...covs.map((n) => covariateValue(a, n))],
                };
            });
            if (config.area.outOfSample) {
                for (const a of candidateAreas) {
                    if (presentSet.has(a) || fittableSet.has(a))
                        continue;
                    if (!covRowByArea.has(a))
                        continue;
                    if (covs.some((n) => !Number.isFinite(covariateValue(a, n))))
                        continue;
                    domainsIn.push({
                        areaId: a,
                        thetaHat: 0,
                        psi: 0,
                        covariates: [1, ...covs.map((n) => covariateValue(a, n))],
                        outOfSample: true,
                    });
                }
            }
            return domainsIn;
        };
        let fit;
        let fitDomains = [];
        let insufficientIssue;
        for (;;) {
            fitDomains = buildDomains(selected);
            try {
                fit = fitFayHerriotImpl(fitDomains, fitConfigFor(config));
                break;
            }
            catch (err) {
                if (err instanceof InsufficientAreasError) {
                    insufficientIssue = err.issue;
                    break;
                }
                const message = err instanceof Error ? err.message : String(err);
                if (/singular/i.test(message)) {
                    const droppable = selected
                        .map((n, i) => ({ n, i }))
                        .filter((x) => !lockedSet.has(x.n))
                        .pop();
                    if (droppable !== undefined) {
                        const next = [...selected];
                        next.splice(droppable.i, 1);
                        lateDrops.push({ name: droppable.n, rule: 'singular-design' });
                        cellIssues.push({
                            code: 'COV_DROPPED',
                            severity: 'warning',
                            cellId: def.cellId,
                            message: `Covariate "${droppable.n}" was dropped because the ` +
                                'model design became singular with it included ' +
                                '(brief 9.14).',
                        });
                        selected = next;
                        continue;
                    }
                    cellIssues.push({
                        code: 'SINGULAR_DESIGN',
                        severity: 'blocking',
                        cellId: def.cellId,
                        message: 'The model design is singular and no further covariate ' +
                            'can be dropped. Direct estimates are reported instead.',
                    });
                    break;
                }
                throw err;
            }
        }
        if (fit === undefined) {
            if (insufficientIssue !== undefined)
                cellIssues.push(insufficientIssue);
            reportDirectOnly();
            const unfitRecord = selectionRecord(selected);
            cells.push({
                ...baseCell,
                selectedCovariates: selected,
                ...(unfitRecord !== undefined ? { selection: unfitRecord } : {}),
            });
            report({ stage: 'cell', completed: 3 + ci, total: totalSteps, cellId: def.cellId });
            continue;
        }
        if (fittableAreas.length < 10) {
            cellIssues.push({
                code: 'LOW_AREA_COUNT',
                severity: 'warning',
                cellId: def.cellId,
                message: `Only ${fittableAreas.length} areas were fitted; model ` +
                    'estimates based on fewer than ten areas are less stable ' +
                    '(brief 9.15, decision S4).',
            });
        }
        if (config.benchmarking.internal) {
            cellIssues.push({
                code: 'INTERNAL_BENCHMARKING_UNSUPPORTED',
                severity: 'warning',
                cellId: def.cellId,
                message: 'Internal consistency benchmarking was requested but is not ' +
                    'applied: no input carries the population share of each ' +
                    'subgroup level within an area (brief 9.13).',
            });
        }
        // ---- Diagnostics mapped to the message catalog (Appendix A). ----
        if (fit.boundary) {
            cellIssues.push({
                code: 'FH_BOUNDARY',
                severity: 'info',
                cellId: def.cellId,
                message: 'The between-area variance reached the boundary at zero; ' +
                    'the adjusted fit was used (brief 9.7).',
            });
        }
        if (fit.diagnostics.rSquaredFh !== undefined && fit.diagnostics.rSquaredFh < 0.1) {
            cellIssues.push({
                code: 'LOW_FIT',
                severity: 'warning',
                cellId: def.cellId,
                message: 'The model explains very little of the variation between ' +
                    'areas (R-squared below 0.10), so the model estimates stay ' +
                    'close to the direct estimates (brief 9.16).',
            });
        }
        if (fit.diagnostics.lowShrinkage) {
            cellIssues.push({
                code: 'HEAVY_SHRINK',
                severity: 'warning',
                cellId: def.cellId,
                message: 'Shrinkage toward the model prediction is heavy in this ' +
                    'cell (median shrinkage weight below 0.10); the model ' +
                    'estimates lean strongly on the covariates (brief 9.16).',
            });
        }
        for (const r of fit.diagnostics.standardizedResiduals) {
            if (r.flagged) {
                cellIssues.push({
                    code: 'OUTLIER',
                    severity: 'warning',
                    cellId: def.cellId,
                    domain: cellDomain(r.areaId),
                    message: `Area ${r.areaId} has a standardized residual of ` +
                        `${r.value.toFixed(2)}, beyond the review threshold of 3 ` +
                        '(brief 9.16).',
                });
            }
        }
        // ---- Benchmarking target (brief 9.13, directive C-5). ---------
        // The target is the population-weighted direct estimate of the
        // cell: sum_i omega_i * phat_i over the in-sample fitted areas,
        // with the population-count weights renormalized over those
        // areas — the same weights the benchmarking constraint uses, and
        // the same construction the bootstrap applies inside replicates
        // and the area-level path applies to its target. (Before C-5 the
        // target was the survey-weighted statewide domain; the two
        // disagree whenever weighted totals differ from population
        // counts — the QC-18 case of review item R9.) Undefined when a
        // fitted area lacks a population count or a direct estimate; the
        // benchmarking step below then skips the cell with an issue.
        let benchmarkTarget;
        {
            let weightSum = 0;
            let weightedDirect = 0;
            let complete = true;
            for (const d of fitDomains) {
                if (d.outOfSample)
                    continue;
                const c = populationCountOf(d.areaId);
                const direct = directByCellArea.get(`${def.cellId}${d.areaId}`);
                if (c === undefined || direct === undefined) {
                    complete = false;
                    break;
                }
                weightedDirect += c * direct.estimate;
                weightSum += c;
            }
            if (complete && weightSum > 0) {
                benchmarkTarget = weightedDirect / weightSum;
            }
        }
        // ---- Back-transformation and uncertainty (9.10, 9.12). ----------
        const confidence = config.uncertainty.confidence;
        let estimates = backTransformAreasImpl(fit, {
            method: config.model.backTransform,
            confidence,
            transform: config.model.transform,
            reportedPrecisionR: config.model.studyReportedPrecisionR ?? false,
        });
        let mseMethod = 'analytical';
        // Directive C-1: a cell fitted by the Li-Lahiri adjusted REML uses
        // bootstrap MSEs in every mode. The analytical Datta-Lahiri MSE
        // omits that estimator's O(1/m) bias term (review item R6), and
        // only the bootstrap reflects the boundary switch's behavior
        // (R5). Exports consume the run's MSEs, so this is what puts
        // bootstrap MSEs for adjusted-REML cells into every export; the
        // methods report names those cells (methods.ts).
        // Study-only variant (Tier 2 Item 8 arm A): studyAnalyticEverywhere
        // routes every cell to the analytical MSE, adjusted-REML cells
        // included, so the study measures the C-1 coupling's cost against
        // the production analytical construction. Absent (every app run),
        // the routing below is the shipped one, unchanged.
        const bootstrapThisCell = config.uncertainty.studyAnalyticEverywhere !== true &&
            (config.uncertainty.mode === 'full' ||
                fit.methodUsed === 'adjusted-reml');
        // Study-only F-3 candidate (frontier workstream F-3, frozen plan
        // §3): when UncertaintyConfig.studyMseCandidate is set, the
        // candidate is the MSE for this cell, replacing both shipped
        // routes. Point estimates are rebuilt from the SAME fit through
        // the SAME back-transformation with only mseTheta replaced, so
        // points, selection, and fit quantities are bit-identical to the
        // control arm (plan §5.5(iii-a)); intervals and RMSEs then flow
        // through the ordinary benchmarking recompute from the
        // candidate mseTheta, exactly as for the shipped constructions.
        // Absent the switch (every app run), this block does not execute.
        const f3CandidateId = config.uncertainty.studyMseCandidate;
        if (f3CandidateId !== undefined) {
            const countsComplete = config.benchmarking.external === 'none' ||
                fitDomains.every((d) => populationCountOf(d.areaId) !== undefined);
            const candBenchmarking = countsComplete
                ? config.benchmarking
                : { external: 'none', internal: false };
            const candCounts = {};
            for (const d of fitDomains) {
                const c = populationCountOf(d.areaId);
                if (c !== undefined)
                    candCounts[d.areaId] = c;
            }
            const cand = f3Candidate(f3CandidateId, fit, fitDomains, {
                seed: `${config.uncertainty.seed}:${def.cellId}`,
                confidence,
                fitConfig: fitConfigFor(config),
                benchmarking: candBenchmarking,
                benchmarkTarget,
                populationCounts: config.benchmarking.external !== 'none' ? candCounts : undefined,
                transform: config.model.transform,
                meanBiasCorrection: config.model.transformBiasCorrection ?? false,
                meanBiasCorrectionForm: config.model.studyTransformBiasCorrectionForm,
            });
            const candByArea = new Map(cand.areas.map((a) => [a.areaId, a]));
            const adjustedFit = {
                ...fit,
                areas: fit.areas.map((a) => ({
                    ...a,
                    mseTheta: candByArea.get(a.areaId)?.mseTheta ?? a.mseTheta,
                })),
            };
            estimates = backTransformAreasImpl(adjustedFit, {
                method: config.model.backTransform,
                confidence,
                transform: config.model.transform,
            });
            if (config.benchmarking.external === 'none') {
                // Unbenchmarked convention (assembleBootstrapEstimates form):
                // the reported standard error is sqrt(mseP) of the candidate.
                estimates = estimates.map((e) => {
                    const c = candByArea.get(e.areaId);
                    if (c === undefined)
                        return e;
                    const rmse = Math.sqrt(Math.max(0, c.mseP));
                    return {
                        ...e,
                        rmse,
                        cv: e.estimate > 0 ? rmse / e.estimate : rmse > 0 ? Number.POSITIVE_INFINITY : 0,
                    };
                });
            }
            mseMethod = f3CandidateId === 'B' ? 'analytical' : 'bootstrap';
        }
        else if (config.uncertainty.studyMsePipelineJ === true) {
            // Study-only Candidate J (Decision 81, Registration R-8,
            // Addendum 94): the pipeline bootstrap MSE on the model scale
            // is the MSE for this cell, replacing both shipped routes,
            // under the F-3 seam's discipline — point estimates are
            // rebuilt from the SAME fit through the SAME
            // back-transformation with only mseTheta replaced, so points,
            // selection, and fit quantities are bit-identical to the
            // control arm; intervals and RMSEs then flow through the
            // ordinary benchmarking recompute from the candidate mseTheta.
            // B = 200 is carried by the construction (the shipped
            // bootstrap's minimum, brief 9.12), not by the harness's
            // bootstrapB, so the control arm's C-1 routing consumes the
            // shipped configuration exactly. Absent the switch (every app
            // run), this block does not execute.
            const countsComplete = config.benchmarking.external === 'none' ||
                fitDomains.every((d) => populationCountOf(d.areaId) !== undefined);
            const candBenchmarking = countsComplete
                ? config.benchmarking
                : { external: 'none', internal: false };
            const candCounts = {};
            for (const d of fitDomains) {
                const c = populationCountOf(d.areaId);
                if (c !== undefined)
                    candCounts[d.areaId] = c;
            }
            const cand = parametricBootstrapMseImpl(fitDomains, {
                replicates: 200,
                seed: `${config.uncertainty.seed}:${def.cellId}`,
                confidence,
                fitConfig: fitConfigFor(config),
                benchmarking: candBenchmarking,
                benchmarkTarget,
                populationCounts: config.benchmarking.external !== 'none' ? candCounts : undefined,
                transform: config.model.transform,
                meanBiasCorrection: config.model.transformBiasCorrection ?? false,
                meanBiasCorrectionForm: config.model.studyTransformBiasCorrectionForm,
                publishedThetaError: true,
            });
            const candByArea = new Map(cand.areas.map((a) => [a.areaId, a]));
            const adjustedFit = {
                ...fit,
                areas: fit.areas.map((a) => ({
                    ...a,
                    mseTheta: candByArea.get(a.areaId)?.mseTheta ?? a.mseTheta,
                })),
            };
            estimates = backTransformAreasImpl(adjustedFit, {
                method: config.model.backTransform,
                confidence,
                transform: config.model.transform,
            });
            if (config.benchmarking.external === 'none') {
                // Unbenchmarked convention (assembleBootstrapEstimates form):
                // the reported standard error is sqrt(mseP) of the candidate.
                estimates = estimates.map((e) => {
                    const c = candByArea.get(e.areaId);
                    if (c === undefined)
                        return e;
                    const rmse = Math.sqrt(Math.max(0, c.mseP));
                    return {
                        ...e,
                        rmse,
                        cv: e.estimate > 0 ? rmse / e.estimate : rmse > 0 ? Number.POSITIVE_INFINITY : 0,
                    };
                });
            }
            mseMethod = 'bootstrap';
        }
        else if (config.uncertainty.studyMseConditionalM === true &&
            !bootstrapThisCell) {
            // Study-only Candidate M (Registration R-11, Addendum 102):
            // the conditional composite — the reviewer's Section 1
            // area-specific conditional MSE estimator, floored and
            // precision-weighted, blended with the shipped model MSE (see
            // conditional-mse.ts for the frozen construction). Funded by
            // the owner against the reviewer's stated condition (Addenda
            // 101-102). Under the F-3 seam's discipline — point estimates
            // are rebuilt from the SAME fit through the SAME
            // back-transformation with only mseTheta replaced, so points,
            // selection, and fit quantities are bit-identical to the
            // control arm; intervals and RMSEs then flow through the
            // ordinary benchmarking recompute from the composite mseTheta.
            // The registered FALLBACK: this branch is entered only where
            // the cell's shipped route is the analytical MSE; where
            // directive C-1 routes the cell to the bootstrap (Full mode or
            // an adjusted-REML fit), bootstrapThisCell holds and the
            // shipped route stands untouched — the composite is an
            // analytic form and does not price the boundary switch. No
            // unbenchmarked rmse override is applied (unlike the F-3/J
            // candidates, whose constructions carry their own bootstrap
            // mseP): the composite carries no mseP of its own, and the
            // back-transformation derives the reported precision from the
            // composite mseTheta exactly as the control arm's analytical
            // route derives it from the shipped mseTheta. Absent the
            // switch (every app run), this block does not execute.
            const compByArea = conditionalCompositeMseTheta(fit);
            const adjustedFit = {
                ...fit,
                areas: fit.areas.map((a) => ({
                    ...a,
                    mseTheta: compByArea.get(a.areaId) ?? a.mseTheta,
                })),
            };
            estimates = backTransformAreasImpl(adjustedFit, {
                method: config.model.backTransform,
                confidence,
                transform: config.model.transform,
            });
            mseMethod = 'analytical';
        }
        else if (bootstrapThisCell) {
            const countsComplete = config.benchmarking.external === 'none' ||
                fitDomains.every((d) => populationCountOf(d.areaId) !== undefined);
            const bootBenchmarking = countsComplete
                ? config.benchmarking
                : { external: 'none', internal: false };
            if (!countsComplete) {
                cellIssues.push({
                    code: 'BENCHMARK_SKIPPED',
                    severity: 'warning',
                    cellId: def.cellId,
                    message: 'Benchmarking was not applied because population counts ' +
                        'are missing for one or more areas in this cell ' +
                        '(brief 9.13).',
                });
            }
            const bootCounts = {};
            for (const d of fitDomains) {
                const c = populationCountOf(d.areaId);
                if (c !== undefined)
                    bootCounts[d.areaId] = c;
            }
            const boot = parametricBootstrapMseImpl(fitDomains, {
                replicates: config.uncertainty.bootstrapB,
                seed: `${config.uncertainty.seed}:${def.cellId}`,
                confidence,
                fitConfig: fitConfigFor(config),
                benchmarking: bootBenchmarking,
                benchmarkTarget,
                populationCounts: config.benchmarking.external !== 'none' ? bootCounts : undefined,
                transform: config.model.transform,
                meanBiasCorrection: config.model.transformBiasCorrection ?? false,
                // Study-only variant (Tier 2 Item 1): mirror the
                // exact-expectation correction term inside the replicates
                // when the study form is selected (see the transform call).
                meanBiasCorrectionForm: config.model.studyTransformBiasCorrectionForm,
                // Study-only variant (Tier 2 Item 9 arm O): the bias-corrected
                // own-directs replicate target. Absent (every app run), the
                // bootstrap forms the production faithful target, unchanged.
                replicateTarget: config.uncertainty.studyReplicateTarget,
            });
            // The bootstrap interval assembly is the engine's single
            // construction (backtransform.ts; Tier 2 Item 7): the
            // Full-mode interval is built on the fitted scale and mapped
            // back (brief 9.10/9.12) — through sin^2 for arcsine fits, as
            // the identity clamped to [0, 1] for linear fits (Q44) — with
            // the reported standard error sqrt(mse_p).
            estimates = assembleBootstrapEstimatesImpl(estimates, fit, boot, {
                confidence,
                transform: config.model.transform,
                reportedPrecisionR: config.model.studyReportedPrecisionR ?? false,
            });
            mseMethod = 'bootstrap';
        }
        // ---- Aggregation check, population-weighted (brief 9.16), and
        // the statewide figures the UI displays (proportion scale). ------
        // The check compares the population-weighted mean of the fitted
        // model estimates (proportion scale, before benchmarking) with
        // the statewide direct estimate. The same means are stored on the
        // cell result for display (cell.statewideFigures); the fit
        // diagnostics' aggregation means are a separate transformed-scale
        // check and are never display values. When a population count is
        // missing for any fitted area, the display figures fall back to
        // unweighted means of the proportion-scale estimates and directs.
        let statewideFigures;
        {
            let weightSum = 0;
            let weightedModel = 0;
            let weightedDirect = 0;
            let sumModel = 0;
            let sumDirect = 0;
            let nAreas = 0;
            let complete = true;
            let directsComplete = true;
            for (const areaFit of fit.areas) {
                if (areaFit.outOfSample)
                    continue;
                const c = populationCountOf(areaFit.areaId);
                const est = estimates.find((e) => e.areaId === areaFit.areaId);
                const direct = directByCellArea.get(`${def.cellId}${areaFit.areaId}`);
                if (c === undefined || est === undefined)
                    complete = false;
                if (direct === undefined)
                    directsComplete = false;
                if (est === undefined || direct === undefined)
                    continue;
                nAreas += 1;
                sumModel += est.estimate;
                sumDirect += direct.estimate;
                if (c !== undefined) {
                    weightedModel += c * est.estimate;
                    weightedDirect += c * direct.estimate;
                    weightSum += c;
                }
            }
            if (complete && weightSum > 0) {
                const modelMean = weightedModel / weightSum;
                if (statewide !== undefined && statewide.estimate !== 0) {
                    const rel = (modelMean - statewide.estimate) / Math.abs(statewide.estimate);
                    if (Math.abs(rel) > 0.05) {
                        cellIssues.push({
                            code: 'AGG_GAP',
                            severity: 'warning',
                            cellId: def.cellId,
                            message: 'The population-weighted model average differs from the ' +
                                `statewide direct estimate by ${(Math.abs(rel) * 100).toFixed(1)}% ` +
                                '(brief 9.16).',
                        });
                    }
                }
                if (statewide !== undefined || directsComplete) {
                    statewideFigures = {
                        modelMean,
                        directEstimate: statewide !== undefined
                            ? statewide.estimate
                            : weightedDirect / weightSum,
                        populationWeighted: true,
                    };
                }
            }
            if (statewideFigures === undefined && nAreas > 0) {
                statewideFigures = {
                    modelMean: sumModel / nAreas,
                    directEstimate: sumDirect / nAreas,
                    populationWeighted: false,
                };
            }
        }
        // ---- External benchmarking (brief 9.13). -------------------------
        let benchmarkApplied = false;
        let benchmarkingMethod;
        if (config.benchmarking.external !== 'none') {
            if (benchmarkTarget === undefined) {
                cellIssues.push({
                    code: 'BENCHMARK_SKIPPED',
                    severity: 'warning',
                    cellId: def.cellId,
                    message: 'Benchmarking was not applied because the population-' +
                        'weighted direct target could not be formed for this ' +
                        'cell: a population count or direct estimate is missing ' +
                        'for one or more fitted areas (brief 9.13, directive C-5).',
                });
            }
            else if (fitDomains.some((d) => populationCountOf(d.areaId) === undefined)) {
                cellIssues.push({
                    code: 'BENCHMARK_SKIPPED',
                    severity: 'warning',
                    cellId: def.cellId,
                    message: 'Benchmarking was not applied because population counts ' +
                        'are missing for one or more areas in this cell ' +
                        '(brief 9.13).',
                });
            }
            else {
                const counts = {};
                for (const d of fitDomains)
                    counts[d.areaId] = populationCountOf(d.areaId);
                try {
                    estimates = applyBenchmarkingImpl(estimates, {
                        method: config.benchmarking.external,
                        target: benchmarkTarget,
                        populationCounts: counts,
                        confidence,
                        transform: config.model.transform,
                        reportedPrecisionR: config.model.studyReportedPrecisionR ?? false,
                    });
                    benchmarkApplied = true;
                    benchmarkingMethod = config.benchmarking.external;
                }
                catch (err) {
                    cellIssues.push({
                        code: 'BENCHMARK_REJECTED',
                        severity: 'warning',
                        cellId: def.cellId,
                        message: 'Benchmarking was not applied to this cell: ' +
                            (err instanceof Error ? err.message : String(err)),
                    });
                }
            }
        }
        // ---- Validated-range flag (directive RB-2, decision D-2). -----
        // Computed on the final model estimates (post-benchmarking) and
        // the respondent counts of the fitted areas; an informational
        // flag only — it never alters an estimate. The RB-4 triggers
        // read the benchmarking outcome of the step above: turned off
        // by the caller is 'user-off', and any engine-side
        // non-application (BENCHMARK_SKIPPED, BENCHMARK_REJECTED) is
        // 'skipped'. The Re-Review 4 no-counts trigger reads whether
        // the design supplied frame PSU counts (the corrected-basis
        // channel): a present, non-empty counts map is 'supplied'.
        const benchmarkingStatus = config.benchmarking.external === 'none'
            ? 'user-off'
            : benchmarkApplied
                ? 'applied'
                : 'skipped';
        const frameCountsStatus = (config.design.psuPopulationCounts?.size ?? 0) > 0
            ? 'supplied'
            : 'not-supplied';
        const validatedRangeConfig = resolveValidatedRangeConfig(config.validation);
        const validatedRange = assessValidatedRange(transformedRows
            .filter((t) => fittableSet.has(t.domain.areaId))
            .map((t) => t.n), estimates.map((e) => e.estimate), validatedRangeConfig, benchmarkingStatus, frameCountsStatus);
        if (validatedRange.flagged) {
            cellIssues.push(outOfValidatedRangeIssue(def.cellId, validatedRange, validatedRangeConfig));
        }
        // ---- Estimate policy, complements, reliability (9.11, 9.17). ----
        const policy = config.policy;
        const nMin = policy.nMin ?? 30;
        const cvMax = policy.cvMax ?? 0.2;
        const estimateByArea = new Map(estimates.map((e) => [e.areaId, e]));
        for (const areaFit of fit.areas) {
            const areaId = areaFit.areaId;
            const model = estimateByArea.get(areaId);
            const direct = directByCellArea.get(`${def.cellId}${areaId}`);
            const t = transformedByCellArea.get(`${def.cellId}${areaId}`);
            if (areaFit.outOfSample && policy.type === 'direct')
                continue;
            let reported;
            let estimateType;
            if (policy.type === 'direct' && direct !== undefined) {
                const cv = direct.estimate > 0
                    ? direct.standardError / direct.estimate
                    : direct.standardError > 0
                        ? Number.POSITIVE_INFINITY
                        : 0;
                reported = {
                    estimate: direct.estimate,
                    lower: direct.interval.lower,
                    upper: direct.interval.upper,
                    rmse: direct.standardError,
                    cv,
                };
                estimateType = 'Direct';
            }
            else if (policy.type === 'hybrid' &&
                direct !== undefined &&
                direct.n >= nMin &&
                (direct.estimate > 0
                    ? direct.standardError / direct.estimate
                    : direct.standardError > 0
                        ? Number.POSITIVE_INFINITY
                        : 0) <= cvMax) {
                const cv = direct.estimate > 0 ? direct.standardError / direct.estimate : 0;
                reported = {
                    estimate: direct.estimate,
                    lower: direct.interval.lower,
                    upper: direct.interval.upper,
                    rmse: direct.standardError,
                    cv,
                };
                estimateType = 'Direct';
            }
            else {
                reported = model;
                estimateType = areaFit.outOfSample ? 'Model-only' : 'Model';
            }
            const width = reported.upper - reported.lower;
            const relWidth = reported.estimate > 0
                ? width / reported.estimate
                : width > 0
                    ? Number.POSITIVE_INFINITY
                    : 0;
            const reliability = {
                n: direct?.n ?? 0,
                effectiveSampleSize: t?.effectiveSampleSize ?? 0,
                degreesOfFreedom: direct?.degreesOfFreedom ?? 0,
                gamma: areaFit.gamma,
                rmse: reported.rmse,
                cv: reported.cv,
                intervalWidth: width,
                relativeIntervalWidth: relWidth,
            };
            let complement;
            const W = populationCountOf(areaId);
            if (W !== undefined) {
                const weightedTotal = Math.round(W);
                const weightedYes = Math.round(reported.estimate * weightedTotal);
                complement = {
                    estimate: 1 - reported.estimate,
                    lower: 1 - reported.upper,
                    upper: 1 - reported.lower,
                    countYes: direct?.yesCount ?? 0,
                    countNo: direct !== undefined ? direct.n - direct.yesCount : 0,
                    weightedYes,
                    weightedNo: weightedTotal - weightedYes,
                };
            }
            else if (direct !== undefined) {
                const weightedTotal = Math.round(direct.weightedTotal);
                const weightedYes = Math.round(reported.estimate * weightedTotal);
                complement = {
                    estimate: 1 - reported.estimate,
                    lower: 1 - reported.upper,
                    upper: 1 - reported.lower,
                    countYes: direct.yesCount,
                    countNo: direct.n - direct.yesCount,
                    weightedYes,
                    weightedNo: weightedTotal - weightedYes,
                };
            }
            recordDomain({
                domain: cellDomain(areaId),
                ...(direct !== undefined ? { direct } : {}),
                model,
                estimateType,
                reliability,
                ...(complement !== undefined ? { complement } : {}),
            }, {
                domain: cellDomain(areaId),
                n: direct?.n ?? 0,
                cv: reported.cv,
                intervalWidth: width,
                relativeIntervalWidth: relWidth,
                psuCount: direct?.psuCount ?? 0,
                effectiveSampleSize: t?.effectiveSampleSize ?? 0,
                degreesOfFreedom: direct?.degreesOfFreedom ?? 0,
                ...(direct !== undefined ? { eventCount: direct.yesCount } : {}),
                ...(estimateType === 'Direct' && direct !== undefined
                    ? standardWidthFields(direct)
                    : {}),
                // The 'sae-standard' preset's inputs (Path to A guide,
                // Part 4.2): the model-based estimate and its reported
                // interval and RMSE, exactly as back-transformation
                // carried them, whatever estimate type the policy
                // reports. Other presets do not read these fields.
                ...(model !== undefined
                    ? {
                        modelEstimate: model.estimate,
                        modelLower: model.lower,
                        modelUpper: model.upper,
                        modelRmse: model.rmse,
                    }
                    : {}),
            });
        }
        // Present areas with respondents but no effective sample (neff 0)
        // never entered the fit; report them direct-only.
        for (const areaId of presentAreas) {
            if (fittableSet.has(areaId))
                continue;
            const already = domains.some((d) => d.domain.cellId === def.cellId && d.domain.areaId === areaId);
            if (already)
                continue;
            const direct = directByCellArea.get(`${def.cellId}${areaId}`);
            const width = direct.interval.upper - direct.interval.lower;
            const relWidth = direct.estimate > 0 ? width / direct.estimate : width > 0 ? Number.POSITIVE_INFINITY : 0;
            const cv = direct.estimate > 0
                ? direct.standardError / direct.estimate
                : direct.standardError > 0
                    ? Number.POSITIVE_INFINITY
                    : 0;
            recordDomain({
                domain: cellDomain(areaId),
                direct,
                estimateType: 'Direct',
                reliability: {
                    n: direct.n,
                    effectiveSampleSize: 0,
                    degreesOfFreedom: direct.degreesOfFreedom,
                    rmse: direct.standardError,
                    cv,
                    intervalWidth: width,
                    relativeIntervalWidth: relWidth,
                },
            }, {
                domain: cellDomain(areaId),
                n: direct.n,
                cv,
                intervalWidth: width,
                relativeIntervalWidth: relWidth,
                psuCount: direct.psuCount,
                effectiveSampleSize: 0,
                degreesOfFreedom: direct.degreesOfFreedom,
                eventCount: direct.yesCount,
                ...standardWidthFields(direct),
            });
        }
        const fittedRecord = selectionRecord(selected);
        cells.push({
            ...baseCell,
            status: 'fitted',
            selectedCovariates: selected,
            ...(fittedRecord !== undefined ? { selection: fittedRecord } : {}),
            fit,
            mseMethod,
            benchmarkApplied,
            ...(benchmarkingMethod !== undefined ? { benchmarkingMethod } : {}),
            ...(statewideFigures !== undefined ? { statewideFigures } : {}),
            outOfValidatedRange: validatedRange.flagged,
            validatedRange: {
                medianRespondentsPerArea: validatedRange.medianRespondentsPerArea,
                ...(validatedRange.smallestModelEstimate !== undefined
                    ? { smallestModelEstimate: validatedRange.smallestModelEstimate }
                    : {}),
                ...(validatedRange.largestModelEstimate !== undefined
                    ? { largestModelEstimate: validatedRange.largestModelEstimate }
                    : {}),
                fittedAreaCount: validatedRange.fittedAreaCount,
                benchmarkingStatus: validatedRange.benchmarkingStatus,
                frameCountsStatus: validatedRange.frameCountsStatus,
            },
        });
        report({ stage: 'cell', completed: 3 + ci, total: totalSteps, cellId: def.cellId });
    }
    // ---- Disclosure rules and complementary suppression (Section 10). -
    const outcomes = applyDisclosureRulesImpl(disclosureInputs, config.disclosure);
    const finalOutcomes = applyComplementarySuppressionImpl(outcomes, disclosureInputs);
    for (let i = 0; i < domains.length; i++) {
        const outcome = finalOutcomes[i];
        domains[i].status = outcome.status;
        if (outcome.status !== 'publish' && outcome.reason !== undefined) {
            domains[i].suppressionReason = outcome.reason;
        }
        // The deciding rule's stable identifier and any labels are
        // stamped for every status, so exports can record the preset
        // outcome per area (Path to A guide, Part 4.2).
        if (outcome.ruleId !== undefined) {
            domains[i].disclosureRuleId = outcome.ruleId;
        }
        if (outcome.labels !== undefined) {
            domains[i].disclosureLabels = outcome.labels;
        }
        if (outcome.status === 'suppressed') {
            domains[i].estimateType = 'Suppressed';
        }
    }
    report({ stage: 'disclosure', completed: totalSteps, total: totalSteps });
    for (const cell of cells)
        globalIssues.push(...cell.issues);
    return { domains, cells, issues: globalIssues, manifest };
}
//# sourceMappingURL=analysis.js.map